import { apiFetch } from '../config/api';
import { debugError } from '../utils/debugLog';

/**
 * Insights Service
 *
 * Handles all insights API operations.
 * Provides methods for generating, saving, and retrieving insights.
 */
class InsightsService {
  /**
   * Get insights
   * @param {Object} options - Query options
   * @param {string} options.period - Time period: 7d, 30d, 90d, or custom (YYYY-MM-DD:YYYY-MM-DD)
   * @param {boolean} options.include_template_effectiveness - Include template effectiveness insights
   * @param {boolean} options.include_error_trends - Include error trend insights
   * @param {boolean} options.include_anomalies - Include anomaly insights
   * @param {boolean} options.include_system_trends - Include system trend insights
   * @param {string} options.template_id - Optional template ID filter
   * @param {string} options.min_severity - Minimum severity: low, medium, high, critical
   * @returns {Promise<Object>} Insights data
   */
  async getInsights(options = {}) {
    const {
      period = '7d',
      include_template_effectiveness = true,
      include_error_trends = true,
      include_anomalies = true,
      include_system_trends = true,
      template_id,
      min_severity = 'low',
    } = options;

    const params = new URLSearchParams({
      period,
      include_template_effectiveness: include_template_effectiveness.toString(),
      include_error_trends: include_error_trends.toString(),
      include_anomalies: include_anomalies.toString(),
      include_system_trends: include_system_trends.toString(),
      min_severity,
    });

    if (template_id) {
      params.append('template_id', template_id);
    }

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/insights/?${params.toString()}`,
        { timeoutMs: 15000 }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch insights:', error);
      throw error;
    }
  }

  /**
   * Get template-specific insights
   * @param {string} templateId - Template ID
   * @param {Object} options - Query options
   * @param {string} options.period - Time period
   * @param {string} options.min_severity - Minimum severity
   * @returns {Promise<Object>} Template insights data
   */
  async getTemplateInsights(templateId, options = {}) {
    const { period = '30d', min_severity = 'low' } = options;
    const params = new URLSearchParams({ period, min_severity });

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/insights/templates/${templateId}?${params.toString()}`,
        { timeoutMs: 10000 }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch template insights:', error);
      throw error;
    }
  }

  /**
   * Get error trend insights
   * @param {string} errorCode - Error code
   * @param {Object} options - Query options
   * @param {string} options.period - Time period
   * @returns {Promise<Object>} Error trend insights data
   */
  async getErrorTrendInsights(errorCode, options = {}) {
    const { period = '30d' } = options;
    const params = new URLSearchParams({ period });

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/insights/errors/${errorCode}?${params.toString()}`,
        { timeoutMs: 10000 }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch error trend insights:', error);
      throw error;
    }
  }

  /**
   * Save insights to database
   * @param {Object} insightsData - Insights data to save
   * @param {string} batchId - Optional batch ID
   * @returns {Promise<Object>} Save response with batch_id
   */
  async saveInsights(insightsData, batchId = null) {
    const params = batchId ? { batch_id: batchId } : {};
    const paramsString = new URLSearchParams(params).toString();
    const url = `/api/v1/templates/analytics/insights/save${paramsString ? `?${paramsString}` : ''}`;

    try {
      const response = await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify(insightsData),
        timeoutMs: 10000,
      });
      return response;
    } catch (error) {
      debugError('Failed to save insights:', error);
      throw error;
    }
  }

  /**
   * List saved insights
   * @param {Object} options - Query options
   * @param {string} options.insight_type - Filter by insight type
   * @param {string} options.scope - Filter by scope (template or system)
   * @param {string} options.severity - Filter by severity
   * @param {string} options.insight_status - Filter by status
   * @param {number} options.limit - Maximum number of results
   * @param {number} options.offset - Offset for pagination
   * @param {string} options.order_by - Field to order by
   * @param {string} options.order_dir - Order direction (ASC or DESC)
   * @returns {Promise<Object>} Saved insights with pagination
   */
  async listSavedInsights(options = {}) {
    const {
      insight_type,
      scope,
      severity,
      insight_status,
      limit = 100,
      offset = 0,
      order_by = 'created_at',
      order_dir = 'DESC',
    } = options;

    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      order_by,
      order_dir,
    });

    if (insight_type) {
      params.append('insight_type', insight_type);
    }
    if (scope) {
      params.append('scope', scope);
    }
    if (severity) {
      params.append('severity', severity);
    }
    if (insight_status) {
      params.append('insight_status', insight_status);
    }

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/insights/saved?${params.toString()}`,
        { timeoutMs: 10000 }
      );
      return response;
    } catch (error) {
      debugError('Failed to list saved insights:', error);
      throw error;
    }
  }

  /**
   * List insight batches
   * @param {Object} options - Query options
   * @param {number} options.limit - Maximum number of results
   * @param {string} options.status - Filter by batch status
   * @param {string} options.insight_type - Filter batches that contain this insight type
   * @param {boolean} options.latest - If true, return only the latest batch
   * @returns {Promise<Object>} Batch list with metadata
   */
  async listBatches(options = {}) {
    const { limit = 10, status, insight_type, latest = false } = options;
    const params = new URLSearchParams({ limit: limit.toString() });
    if (status) {
      params.append('status', status);
    }
    if (insight_type) {
      params.append('insight_type', insight_type);
    }
    if (latest) {
      params.append('latest', 'true');
    }

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/insights/saved/batches?${params.toString()}`,
        { timeoutMs: 10000 }
      );
      return response;
    } catch (error) {
      debugError('Failed to list batches:', error);
      throw error;
    }
  }

  /**
   * Get insights for a specific batch
   * @param {string} batchId - Batch ID
   * @param {Object} options - Query options
   * @param {number} options.limit - Maximum number of results
   * @returns {Promise<Object>} Batch insights data
   */
  async getBatchInsights(batchId, options = {}) {
    const { limit = 100 } = options;
    const params = new URLSearchParams({ limit: limit.toString() });

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/insights/saved/batches/${batchId}?${params.toString()}`,
        { timeoutMs: 10000 }
      );
      return response;
    } catch (error) {
      debugError('Failed to get batch insights:', error);
      throw error;
    }
  }
}

const insightsService = new InsightsService();
export default insightsService;
